import { useState } from "react";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { useRefresh, triggerRefresh } from "@/hooks/useRefresh";
import { fmtDate, num } from "@/lib/format";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, X, ArrowRight, CheckCircle2 } from "lucide-react";

const newLine = () => ({ _key: Math.random().toString(36).slice(2), item_id: "", qty: "" });

export default function Transfers() {
  const { auth } = useAuth();
  const role = auth?.membership?.role;
  const assigned = auth?.membership?.branch_ids || [];
  const { branches, writableBranches } = useBranch();
  const [transfers, setTransfers] = useState([]);
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ from_branch_id: "", to_branch_id: "", lines: [newLine()] });

  const destBranches = branches.filter((b) => b.branch_id !== form.from_branch_id);

  useRefresh(() => {
    api.get("/transfers").then((r) => setTransfers(r.data));
    api.get("/items").then((r) => setItems(r.data));
  }, []);

  const canCreate = role === "owner" || role === "manager";
  const canReceive = (t) => role === "owner" || assigned.includes(t.to_branch_id);

  const setLine = (i, k, v) => {
    const lines = [...form.lines];
    lines[i] = { ...lines[i], [k]: v };
    setForm({ ...form, lines });
  };

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/transfers", {
        ...form,
        lines: form.lines.filter((l) => l.item_id && l.qty).map((l) => ({ item_id: l.item_id, qty: parseFloat(l.qty) })),
      });
      toast.success("Transfer sent — stock deducted from source");
      setOpen(false);
      setForm({ from_branch_id: "", to_branch_id: "", lines: [newLine()] });
      triggerRefresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const receive = async (t) => {
    try {
      await api.post(`/transfers/${t.transfer_id}/receive`);
      toast.success("Transfer received — stock added to destination");
      triggerRefresh();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div className="space-y-4" data-testid="transfers-page">
      <div className="flex flex-wrap items-center gap-3 justify-between">
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">Transfers</h1>
        {canCreate && <Button onClick={() => setOpen(true)} data-testid="new-transfer-btn"><Plus className="h-4 w-4 mr-1" /> New transfer</Button>}
      </div>
      <div className="grid gap-3">
        {transfers.length === 0 && <p className="text-sm text-muted-foreground">No transfers yet.</p>}
        {transfers.map((t) => (
          <div key={t.transfer_id} className="border rounded-xl bg-card p-4" data-testid={`transfer-card-${t.transfer_id}`}>
            <div className="flex flex-wrap items-center gap-3 justify-between">
              <div className="flex items-center gap-2 text-sm font-medium">
                {t.from_branch_name} <ArrowRight className="h-4 w-4 text-muted-foreground" /> {t.to_branch_name}
              </div>
              <div className="flex items-center gap-2">
                <Badge className={t.status === "received" ? "bg-green-100 text-green-800 hover:bg-green-100" : "bg-amber-100 text-amber-800 hover:bg-amber-100"}>
                  {t.status === "received" ? "Received" : "In transit"}
                </Badge>
                {t.status === "in_transit" && canReceive(t) && (
                  <Button size="sm" variant="outline" onClick={() => receive(t)} data-testid={`receive-transfer-${t.transfer_id}`}>
                    <CheckCircle2 className="h-4 w-4 mr-1" /> Confirm receive
                  </Button>
                )}
              </div>
            </div>
            <div className="mt-2 text-xs text-muted-foreground">
              Sent by {t.sent_by} · {fmtDate(t.sent_at)}{t.received_by ? ` · Received by ${t.received_by} · ${fmtDate(t.received_at)}` : ""}
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {t.lines.map((l, i) => (
                <span key={`${l.item_id}-${i}`} className="text-xs bg-slate-100 rounded px-2 py-1">{l.item_name}: {num(l.qty)} {l.unit}</span>
              ))}
            </div>
          </div>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="transfer-dialog">
          <DialogHeader><DialogTitle>New branch transfer</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2"><Label>From</Label>
                <Select value={form.from_branch_id} onValueChange={(v) => setForm({ ...form, from_branch_id: v })}>
                  <SelectTrigger data-testid="transfer-from-select"><SelectValue placeholder="Source" /></SelectTrigger>
                  <SelectContent>{writableBranches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
                </Select></div>
              <div className="space-y-2"><Label>To</Label>
                <Select value={form.to_branch_id} onValueChange={(v) => setForm({ ...form, to_branch_id: v })}>
                  <SelectTrigger data-testid="transfer-to-select"><SelectValue placeholder="Destination" /></SelectTrigger>
                  <SelectContent>{destBranches.map((b) => <SelectItem key={b.branch_id} value={b.branch_id}>{b.name}</SelectItem>)}</SelectContent>
                </Select></div>
            </div>
            <div className="space-y-2">
              <Label>Items</Label>
              {form.lines.map((l, i) => (
                <div key={l._key} className="flex gap-2">
                  <Select value={l.item_id} onValueChange={(v) => setLine(i, "item_id", v)}>
                    <SelectTrigger className="flex-1" data-testid={`transfer-line-item-${i}`}><SelectValue placeholder="Item" /></SelectTrigger>
                    <SelectContent className="max-h-64">{items.map((it) => <SelectItem key={it.item_id} value={it.item_id}>{it.name} ({it.unit})</SelectItem>)}</SelectContent>
                  </Select>
                  <Input type="number" step="any" min="0" placeholder="Qty" className="w-24" value={l.qty}
                    onChange={(e) => setLine(i, "qty", e.target.value)} data-testid={`transfer-line-qty-${i}`} />
                  {form.lines.length > 1 && (
                    <Button type="button" size="icon" variant="ghost" onClick={() => setForm({ ...form, lines: form.lines.filter((_, j) => j !== i) })}>
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              ))}
              <Button type="button" size="sm" variant="outline" onClick={() => setForm({ ...form, lines: [...form.lines, newLine()] })} data-testid="transfer-add-line-btn">
                <Plus className="h-3.5 w-3.5 mr-1" /> Add line
              </Button>
            </div>
            <Button type="submit" className="w-full" disabled={!form.from_branch_id || !form.to_branch_id} data-testid="transfer-submit-btn">
              Send transfer
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
